import * as geom from "js/core/utilities/geom";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { TextElement } from "js/elements/base/TextElement";
import { SVGPolygonElement, SVGPolylineElement } from "js/elements/base/SVGElement";
import { HorizontalAlignType } from "common/constants";
import { getValueOrDefault } from "js/core/utilities/extensions";
import { app } from "js/namespaces";

class Pyramid extends CollectionElement {
    getChildItemType() {
        return PyramidSection;
    }

    get defaultItemData() {
        return {
            title: { text: "" },
            body: { text: "" }
        };
    }

    _calcProps(props, options) {
        this.bodyTextScale = 1;
        let { size, children, styles } = props;

        const calcLayout = () => {
            let gap = this.styles.gap || 0;

            let pyramidCapSize = size.width * (this.model.capSize || 0) / 100;
            let pyramidBaseSize = size.width * (this.model.baseSize != undefined ? this.model.baseSize : 50) / 100;
            if (this.model.inverted) {
                const capSize = pyramidCapSize;
                pyramidCapSize = pyramidBaseSize;
                pyramidBaseSize = capSize;
            }

            let pyramidWidth = Math.max(pyramidBaseSize, pyramidCapSize);

            // calculate the section heights and total height of pyramid
            let sectionHeight = Math.max(this.styles.minItemHeight, Math.floor(size.height / this.itemCount)) - gap;
            let totalHeight = (sectionHeight + gap) * this.itemCount - gap;
            if (totalHeight > size.height) {
                props.isFit = false;
            }

            // calculate the pyramid width
            let maxPyramidWidth = size.width;
            let descriptionWidth = 0;
            if (this.model.showDescription) {
                maxPyramidWidth -= (this.styles.minDescriptionWidth || 300);
                pyramidWidth = Math.min(pyramidWidth, maxPyramidWidth);

                descriptionWidth = Math.min(this.styles.maxDescriptionWidth || 500, size.width - pyramidWidth);
                if (pyramidCapSize > pyramidBaseSize) {
                    pyramidCapSize = pyramidWidth;
                } else {
                    pyramidBaseSize = pyramidWidth;
                }
            }

            pyramidCapSize = Math.min(pyramidCapSize, maxPyramidWidth);
            pyramidBaseSize = Math.min(pyramidBaseSize, maxPyramidWidth);

            // total width is pyramid width + any description width
            let totalWidth = pyramidWidth + descriptionWidth;
            let step = this.model.step;

            // calc props for the sections
            let y = 0;
            for (let i = 0; i < this.itemElements.length; i++) {
                let slope = 1 / (totalHeight / (pyramidBaseSize - pyramidCapSize));
                let topWidth = pyramidCapSize + slope * i / this.itemCount * totalHeight;
                let botWidth = pyramidCapSize + slope * ((i + 1) / this.itemCount * totalHeight - gap);

                if (this.model.step) {
                    topWidth = botWidth = Math.max(topWidth, botWidth);
                }
                let midWidth = 0.5 * (topWidth + botWidth);

                let offsetX = this.model.showDescription ? 0 : size.width / 2 - totalWidth / 2;

                let section = children.setProps(this.itemElements[i], new geom.Size(totalWidth, sectionHeight), {
                    pyramidWidth, topWidth, botWidth, midWidth, gap, step,
                    showDescription: this.model.showDescription,
                    capAlign: this.model.capAlign
                });
                section.bounds = new geom.Rect(offsetX, y, section.size);

                y += sectionHeight + gap;
            }
        };

        calcLayout();
        if (this.bodyTextScale < 1) {
            calcLayout(); // recalc using the bodyTextScale
        }

        return { size };
    }
}

class PyramidSection extends CollectionItemElement {
    get selectionPadding() {
        return 0;
    }

    get name() {
        return "Section";
    }

    _build() {
        this.shape = this.addElement("shape", () => SVGPolygonElement);
        this.title = this.addElement("title", () => TextElement, {
            model: this.model,
            autoHeight: true,
            scaleTextToFit: true,
            constrainWidth: true
        });
        if (this.parentElement.model.showDescription) {
            if (!this.parentElement.model.step) {
                this.rule = this.addElement("rule", () => SVGPolylineElement);
            }
            this.body = this.addElement("body", () => TextElement, {
                model: this.model,
                autoHeight: true,
                scaleTextToFit: true,
            });
        }
    }

    _calcProps(props, options) {
        const { size, children, styles } = props;

        let sectionPath;
        let ruleX;
        let titleX;
        let bodyX;

        let textAlign;

        switch (options.capAlign || "center") {
            case "center":
                sectionPath = [
                    [(options.pyramidWidth - options.topWidth) / 2, 0],
                    [(options.pyramidWidth + options.topWidth) / 2, 0],
                    [(options.pyramidWidth + options.botWidth) / 2, size.height],
                    [(options.pyramidWidth - options.botWidth) / 2, size.height],
                    [(options.pyramidWidth - options.topWidth) / 2, 0]
                ];
                ruleX = options.pyramidWidth / 2 + options.topWidth / 2 + 20;
                titleX = (options.pyramidWidth - options.midWidth) / 2;
                bodyX = (options.pyramidWidth + Math.max(options.botWidth, options.topWidth)) / 2 + 20;
                this.title.styles.textAlign = HorizontalAlignType.CENTER;
                break;
            case "left":
                sectionPath = [
                    [0, 0],
                    [options.topWidth, 0],
                    [options.botWidth, size.height],
                    [0, size.height],
                    [0, 0]
                ];
                ruleX = options.topWidth + 20;
                titleX = 20;
                bodyX = Math.max(options.botWidth, options.topWidth) + 20;

                if (this.parentElement.model.step) {
                    options.midWidth -= 40;
                }

                this.title.styles.textAlign = HorizontalAlignType.LEFT;
                break;
        }

        const title = children.setProps(this.title, new geom.Size(options.midWidth, size.height));
        title.bounds = new geom.Rect(titleX, size.height / 2 - title.size.height / 2, options.midWidth, title.size.height);

        if (options.showDescription) {
            const bodySize = new geom.Size(
                size.width - ((options.pyramidWidth + Math.max(options.botWidth, options.topWidth)) / 2 + 20),
                size.height
            );
            const body = children.setProps(
                this.body,
                bodySize,
                { forceTextScale: this.parentElement.bodyTextScale }
            );
            body.bounds = new geom.Rect(bodyX, size.height / 2 - body.size.height / 2, body.size);
            this.parentElement.bodyTextScale = Math.min(this.parentElement.bodyTextScale, this.body.textScale);
        }

        const shapeProps = children.setProps(this.shape, size);
        shapeProps.path = sectionPath;
        shapeProps.layer == -1;

        if (this.shape.styles.fillColor != "none" && this.shadeColors && this.model.color == null) {
            this.calcShadedColors(this.shape);
        }

        if (options.showDescription) {
            if (!this.parentElement.model.step) {
                let ruleY;
                if (this.itemIndex == 0) {
                    ruleY = 1;
                } else {
                    ruleY = -options.gap / 2;
                }
                const ruleProps = children.setProps(this.rule, size);
                ruleProps.path = [[ruleX, ruleY], [size.width, ruleY]];
            }
        }

        return { size };
    }

    getBackgroundColor(forElement) {
        if (forElement && forElement.id == "title" && this.styles.shape.fillColor != "none") {
            return this.getShapeFillColor(this.shape);
        } else {
            return this.getParentBackgroundColor();
        }
    }
}

export const elements = {
    Pyramid,
};
